import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Input from '../../components/ui/Input';
import StatCard from '../../components/ui/StatCard';
import GradientIcon from '../../components/ui/GradientIcon';
import { Building2, CheckCircle2, Clock, ShieldCheck } from 'lucide-react';
import Modal from '../../components/ui/Modal';
import Pagination from '../../components/ui/Pagination';
import { organizationService } from '../../services';
import exportService from '../../services/exportService';

/**
 * Organizations Management Page (Super Admin)
 * Manage all organizations on the platform
 */

const OrganizationsPage = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, organization: null });
  const [suspendModal, setSuspendModal] = useState({ isOpen: false, organization: null, reason: '', loading: false });
  const [reactivateModal, setReactivateModal] = useState({ isOpen: false, organization: null, loading: false });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchOrganizations();
  }, [filterPlan, filterStatus]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const params = { limit: 100 };
      if (filterPlan !== 'all') params.plan = filterPlan;
      // 'expired' is derived from subscription, not org.status — don't send it
      // to backend (org.status enum has no 'expired'). Filter client-side via
      // the planExpired flag the backend attaches to each row.
      if (filterStatus !== 'all' && filterStatus !== 'expired') params.status = filterStatus;
      if (searchTerm) params.search = searchTerm;

      const response = await organizationService.getOrganizations(params);

      // Transform API data to match frontend format
      const transformedOrgs = response.data.map(org => ({
        id: org._id,
        name: org.name,
        plan: org.plan || 'free',
        status: org.status || 'active',
        planExpired: !!org.planExpired,
        trainers: org.trainers || org.trainersCount || 0,
        sessions: org.sessions || org.sessionsCount || 0,
        totalParticipants: org.totalParticipants || org.usage?.totalParticipants || 0,
        storageUsed: org.storageUsed || 0,
        storageLimit: org.storageLimit || 100,
        createdAt: new Date(org.createdAt).toLocaleDateString(),
        contactEmail: org.contactEmail || org.email || '',
        contactName: org.contactName || '',
        renewalDate: org.renewalDate ? new Date(org.renewalDate).toLocaleDateString() : null,
      }));

      setOrganizations(transformedOrgs);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  // Filter organizations
  const filteredOrganizations = organizations.filter((org) => {
    const matchesSearch =
      searchTerm === '' ||
      org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.contactEmail.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPlan = filterPlan === 'all' || org.plan === filterPlan;
    const matchesStatus =
      filterStatus === 'all'
        ? true
        : filterStatus === 'expired'
          ? org.planExpired
          : org.status === filterStatus;

    return matchesSearch && matchesPlan && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredOrganizations.length / itemsPerPage);
  const paginatedOrganizations = filteredOrganizations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterPlan, filterStatus]);

  // Get stats
  const stats = {
    total: organizations.length,
    active: organizations.filter((o) => o.status === 'active').length,
    expired: organizations.filter((o) => o.planExpired).length,
    notExpired: organizations.filter((o) => !o.planExpired).length,
    free: organizations.filter((o) => o.plan === 'free').length,
    pro: organizations.filter((o) => o.plan === 'pro').length,
  };

  // Plan labels and colors matching Dashboard subscription breakdown
  const PLAN_CONFIG = {
    free: { label: 'Free', bg: 'bg-slate-100', text: 'text-slate-600' },
    pro: { label: 'Pro', bg: 'bg-blue-50', text: 'text-blue-600' },
    pro_plus: { label: 'Pro Plus', bg: 'bg-cyan-50', text: 'text-cyan-600' },
    enterprise_starter: { label: 'Enterprise Starter', bg: 'bg-amber-50', text: 'text-amber-600' },
    enterprise_scale: { label: 'Enterprise Scale', bg: 'bg-green-50', text: 'text-green-600' },
  };

  const getPlanLabel = (plan) => PLAN_CONFIG[plan]?.label || plan;
  const getPlanClasses = (plan) => {
    const config = PLAN_CONFIG[plan] || PLAN_CONFIG.free;
    return `${config.bg} ${config.text}`;
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active':
        return 'live';
      case 'inactive':
        return 'default';
      case 'expiring':
        return 'warning';
      case 'suspended':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleDeleteClick = (org) => {
    setDeleteModal({ isOpen: true, organization: org });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.organization) return;

    setIsDeleting(true);
    try {
      await organizationService.deleteOrganization(deleteModal.organization.id);

      // Remove from local state
      setOrganizations((prev) => prev.filter((org) => org.id !== deleteModal.organization.id));

      toast.success(`${deleteModal.organization.name} has been deleted`);
      setDeleteModal({ isOpen: false, organization: null });
    } catch (error) {
      toast.error(error.message || 'Failed to delete organization');
      console.error('Error deleting organization:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, organization: null });
  };

  const handleToggleSuspend = (org) => {
    if (org.status === 'suspended') {
      // Open reactivate modal
      setReactivateModal({ isOpen: true, organization: org, loading: false });
    } else {
      // Open suspend modal
      setSuspendModal({ isOpen: true, organization: org, reason: '', loading: false });
    }
  };

  const handleReactivateConfirm = async () => {
    if (!reactivateModal.organization) return;

    setReactivateModal((prev) => ({ ...prev, loading: true }));
    try {
      await organizationService.reactivateOrganization(reactivateModal.organization.id);
      setOrganizations((prev) =>
        prev.map((o) => (o.id === reactivateModal.organization.id ? { ...o, status: 'active' } : o))
      );
      toast.success(`${reactivateModal.organization.name} has been reactivated`);
      setReactivateModal({ isOpen: false, organization: null, loading: false });
    } catch (error) {
      toast.error(error.message || 'Failed to reactivate organization');
      setReactivateModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleSuspendConfirm = async () => {
    if (!suspendModal.reason.trim() || !suspendModal.organization) return;

    setSuspendModal((prev) => ({ ...prev, loading: true }));
    try {
      await organizationService.suspendOrganization(suspendModal.organization.id, suspendModal.reason.trim());
      setOrganizations((prev) =>
        prev.map((o) => (o.id === suspendModal.organization.id ? { ...o, status: 'suspended' } : o))
      );
      toast.success(`${suspendModal.organization.name} has been suspended`);
      setSuspendModal({ isOpen: false, organization: null, reason: '', loading: false });
    } catch (error) {
      toast.error(error.message || 'Failed to suspend organization');
      setSuspendModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // Handle export
  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportService.exportOrganizationsCSV();
      toast.success('Organizations exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Failed to export organizations');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DashboardLayout title="Organizations">
      <div className="p-4 pb-20 sm:pb-6 space-y-4">
        {/* Header */}
        <div className="mb-3">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="inline-flex items-center gap-1.5 text-neutral-600 hover:text-neutral-900 transition-colors mb-3 group"
          >
            <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">Back to Dashboard</span>
          </button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-neutral-900">Organizations</h1>
            <p className="text-xs text-neutral-600">Manage all organizations on the platform</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleExport} disabled={isExporting}>
              {isExporting ? (
                <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              )}
              <span className="hidden sm:inline ml-1.5">{isExporting ? 'Exporting...' : 'Export'}</span>
            </Button>
            <Button variant="primary" size="sm" onClick={() => navigate('/admin/organizations/new')}>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="hidden sm:inline ml-1.5">Add Organization</span>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard label="Total Orgs"  value={stats.total}      icon={Building2}    variant="blue" />
          <StatCard label="Active"      value={stats.active}     icon={CheckCircle2} variant="emerald" />
          <StatCard label="Expired"     value={stats.expired}    icon={Clock}        variant="rose" />
          <StatCard label="Plan Active" value={stats.notExpired} icon={ShieldCheck}  variant="violet" />
        </div>

        {/* Organizations Table Card */}
        <div className="bg-white rounded-2xl border border-neutral-100 overflow-hidden">
          {/* Search and Filters inside card */}
          <div className="p-5 border-b border-neutral-100">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="relative flex-1 w-full sm:max-w-md">
                <svg
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all bg-neutral-50/50 placeholder:text-neutral-400"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <select
                  value={filterPlan}
                  onChange={(e) => setFilterPlan(e.target.value)}
                  className="flex-1 sm:flex-none px-3 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white appearance-none pr-8 cursor-pointer text-neutral-700"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                >
                  <option value="all">All Plans</option>
                  <option value="free">Free</option>
                  <option value="pro">Pro</option>
                  <option value="pro_plus">Pro Plus</option>
                  <option value="enterprise_starter">Enterprise Starter</option>
                  <option value="enterprise_scale">Enterprise Scale</option>
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="flex-1 sm:flex-none px-3 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white appearance-none pr-8 cursor-pointer text-neutral-700"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="expired">Expired</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-neutral-100">
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Organization</th>
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Status</th>
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Plan</th>
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Trainers</th>
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Sessions</th>
                  <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Participants</th>
                  <th className="text-right py-3.5 px-5 text-sm font-medium text-neutral-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrganizations.map((org) => (
                  <motion.tr
                    key={org.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border-b border-neutral-50 hover:bg-neutral-50/60 transition-colors cursor-pointer"
                    onClick={() => navigate(`/admin/organizations/${org.id}`)}
                  >
                    <td className="py-4 px-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center text-primary-600 font-semibold text-sm flex-shrink-0">
                          {org.name.charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-neutral-900 truncate">{org.name}</p>
                          <p className="text-xs text-neutral-400 truncate">{org.contactEmail}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-5">
                      <Badge variant={getStatusBadgeVariant(org.status)} size="sm">
                        {org.status ? org.status.charAt(0).toUpperCase() + org.status.slice(1) : 'Active'}
                      </Badge>
                    </td>
                    <td className="py-4 px-5">
                      {org.planExpired ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-600">
                          Expired
                        </span>
                      ) : (
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getPlanClasses(org.plan)}`}>
                          {getPlanLabel(org.plan)}
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-5">
                      <span className="text-sm text-neutral-700">{org.trainers}</span>
                    </td>
                    <td className="py-4 px-5">
                      <span className="text-sm text-neutral-700">{org.sessions}</span>
                    </td>
                    <td className="py-4 px-5">
                      <span className="text-sm text-neutral-700">{org.totalParticipants.toLocaleString()}</span>
                    </td>
                    <td className="py-4 px-5 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-0.5">
                        <button
                          onClick={() => navigate(`/admin/organizations/${org.id}`)}
                          className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
                          title="View"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => navigate(`/admin/organizations/${org.id}/edit`)}
                          className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleToggleSuspend(org)}
                          className={`p-2 rounded-lg transition-colors ${
                            org.status === 'suspended'
                              ? 'text-green-500 hover:text-green-700 hover:bg-green-50'
                              : 'text-neutral-400 hover:text-amber-600 hover:bg-amber-50'
                          }`}
                          title={org.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                        >
                          {org.status === 'suspended' ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteClick(org)}
                          className="p-2 rounded-lg text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>

            {paginatedOrganizations.length === 0 && (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-neutral-100 rounded-full mb-4">
                  <svg className="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <p className="text-sm text-neutral-600 font-medium mb-1">No organizations found</p>
                <p className="text-xs text-neutral-400">Try adjusting your search or filters</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredOrganizations.length > 0 && (
            <div className="px-3 sm:px-5 py-3 sm:py-3.5 border-t border-neutral-100">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                totalItems={filteredOrganizations.length}
              />
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        title="Delete Organization"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-neutral-700">
                Are you sure you want to delete <span className="font-semibold">{deleteModal.organization?.name}</span>?
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                This action cannot be undone. All data associated with this organization including trainers, sessions, and participants will be permanently removed.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-neutral-200">
            <Button variant="ghost" size="sm" onClick={handleDeleteCancel} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
            >
              {isDeleting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-1.5 h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Deleting...
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Organization
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Suspend Organization Modal */}
      <Modal
        isOpen={suspendModal.isOpen}
        onClose={() => setSuspendModal({ isOpen: false, organization: null, reason: '', loading: false })}
        title="Suspend Organization"
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setSuspendModal({ isOpen: false, organization: null, reason: '', loading: false })}
              disabled={suspendModal.loading}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleSuspendConfirm}
              disabled={!suspendModal.reason.trim() || suspendModal.loading}
            >
              {suspendModal.loading ? 'Suspending...' : 'Suspend'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-100 rounded-xl">
            <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-red-800">
                You are about to suspend <strong>{suspendModal.organization?.name}</strong>
              </p>
              <p className="text-xs text-red-600 mt-1">
                All users in this organization will lose access immediately. They will see the suspension reason on their dashboard.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">
              Reason for suspension <span className="text-red-500">*</span>
            </label>
            <textarea
              value={suspendModal.reason}
              onChange={(e) => setSuspendModal((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder="e.g., Violation of terms of service, Non-payment, etc."
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2.5 border border-neutral-300 rounded-xl text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              autoFocus
            />
            <p className="text-xs text-neutral-400 mt-1 text-right">{suspendModal.reason.length}/500</p>
          </div>
        </div>
      </Modal>

      {/* Reactivate Organization Modal */}
      <Modal
        isOpen={reactivateModal.isOpen}
        onClose={() => setReactivateModal({ isOpen: false, organization: null, loading: false })}
        title="Reactivate Organization"
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setReactivateModal({ isOpen: false, organization: null, loading: false })}
              disabled={reactivateModal.loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleReactivateConfirm}
              disabled={reactivateModal.loading}
            >
              {reactivateModal.loading ? 'Reactivating...' : 'Reactivate'}
            </Button>
          </>
        }
      >
        <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-100 rounded-xl">
          <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-green-800">
              You are about to reactivate <strong>{reactivateModal.organization?.name}</strong>
            </p>
            <p className="text-xs text-green-600 mt-1">
              All users in this organization will regain access to their accounts immediately.
            </p>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default OrganizationsPage;
